/**
 * EventsPage - Dashboard de Eventos
 * 
 * Página principal com estatísticas e visão geral do sistema de eventos
 */

import { type FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StandardPage from '@/components/Common/StandardPage';
import { EventHealthBadge } from '@/components/Common/EventHealthBadge';
import { Loading } from '@/components/Common/Loading';
import { eventService } from '@/services';
import type { EventStatisticsResponse, EventHealthResponse } from '@/types';
import { MdOutlineWarning } from '@/config/icons';
import styles from './EventsPage.module.css';

export const EventsPage: FC = () => {
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState<EventStatisticsResponse | null>(null);
  const [health, setHealth] = useState<EventHealthResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const [statsData, healthData] = await Promise.all([
        eventService.getStatistics(),
        eventService.getHealth(),
      ]);
      setStatistics(statsData);
      setHealth(healthData);
    } catch (err) {
      setError('Erro ao carregar dados do sistema de eventos');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <StandardPage
      header={{
        title: "Sistema de Eventos",
        subtitle: "Monitoramento e gerenciamento de eventos de agentes",
      }}
      error={error ? new Error(error) : null}
    >
      {/* Health Badge */}
      <div className={styles.healthSection}>
        <EventHealthBadge status={health?.status} isLoading={!health} />
        {health && (
          <p className={styles.recommendation}>{health.recommendation}</p>
        )}
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <h3>Total de Eventos</h3>
            <p className={styles.statValue}>{statistics.totalEvents.toLocaleString()}</p>
          </div>

          <div className={styles.statCard} data-warning={statistics.pendingEvents > 1000}>
            <h3>Eventos Pendentes</h3>
            <p className={styles.statValue}>{statistics.pendingEvents.toLocaleString()}</p>
            {statistics.pendingEvents > 1000 && (
              <span className={styles.warning}>
                <MdOutlineWarning /> Alto volume
              </span>
            )}
          </div>

          <div className={styles.statCard}>
            <h3>Eventos Processados</h3>
            <p className={styles.statValue}>{statistics.processedEvents.toLocaleString()}</p>
          </div>

          <div className={styles.statCard}>
            <h3>Tempo Médio</h3>
            <p className={styles.statValue}>
              {statistics.averageProcessingTime 
                ? `${Math.round(statistics.averageProcessingTime)}s`
                : '-'}
            </p>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className={styles.actionsGrid}>
        <button
          className={styles.actionCard}
          onClick={() => navigate('/events/details')}
        >
          <h4>Ver Todos os Eventos</h4>
          <p>Listagem completa com filtros</p>
        </button>

        <button
          className={styles.actionCard}
          onClick={() => navigate('/events/stuck')}
          data-alert={health && health.stuckEvents > 0}
        >
          <h4>Eventos Travados</h4>
          <p>{health?.stuckEvents || 0} eventos com problemas</p>
        </button>

        <button
          className={styles.actionCard}
          onClick={() => navigate('/events/management')}
        >
          <h4>Gerenciamento</h4>
          <p>Limpeza e manutenção</p>
        </button>
      </div>
    </StandardPage>
  );
};

export default EventsPage;
